import {useMutation, useQueries, useQuery} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {
  addKeywordHighlight,
  addRemarks,
  canDeleteCodingForm,
  copyCodingForm,
  createDocView,
  createMasterDocList,
  deleteAnnotation,
  deleteCodingForm,
  deleteCodingStamp,
  deleteKeywordHightlight,
  deleteRemarks,
  getAnalyticsDocuments,
  getAnnotationDetails,
  getAnnotationListGrid,
  getAudioFilePath,
  getAudioWaveExist,
  getAudioWaveZoomInOut,
  getCodingFormDetailsforEdit,
  getCodingFormGridList,
  getCodingFormList,
  getCodingFormMetadata,
  getCodingStamp,
  getDefaultRedactionPrompt,
  getDocDetailsMetadataGridData,
  getDocDownloadFormats,
  getDocExplorerColumnConfig,
  getDocExplorerGridData,
  getDocIsCompleted,
  getDocListGridData,
  getDocListMetaData,
  getDocListmetadataFilters,
  getDocMetadataFilterOptionsList,
  getDocMetadataFilters,
  getDocumentList,
  getDocumentViewerCodingForm,
  getDocumentViewerCodingFormList,
  getDocumentViewerListId,
  getDocumentViewerManualSort,
  getDuplicateDocumentsForSelectedDocumentId,
  getEditCodingDetails,
  getEditCodingStamp,
  getFilePath,
  getPreviewDocument,
  getFolderListView,
  getKeywordDetails,
  getKeywordHighlightColorList,
  getKeywordHighlightList,
  getMediaForStreaming,
  getMediaTranscript,
  getMetadataListDoclistFilters,
  getRedactionPanelStatus,
  getRedactionPropagation,
  getRedactionTags,
  getRefreshCodingStamp,
  getRemarks,
  getTextViewDoc,
  getTranslations,
  getTreeNodesById,
  getUserRedactionTags,
  getViewingSessionId,
  pinCodingForm,
  postDocumentViewerOptimizedSort,
  postDocumentViewerSort,
  postDocumentViewerSortConfig,
  renderThreadMap,
  resetDocColumnConfig,
  saveCodingForm,
  saveCodingFormSortOrder,
  saveDocColumnConfig,
  saveEditCodingStamp,
  saveRedactionTags,
  saveUserCodingStamp,
  setDefaultRedactionPrompt,
  upsertAnnotation,
  saveToProfile,
  getDocumentListView,
  codeSameAsPrevious,
  getProgressBar,
  getRefreshAssignmentProgress,
  getConceptResults,
  addNavigationNotification,
  getDocumentForTagAndFolder,
  getNavigationMasterListId,
  getDocumentListIdForAction,
  getDocumentFiles,
} from '../../api/endpoints/documentApi';
import {
  analyticsDocPayload,
  DocExplorerMasterRequest,
  DocExplorerMasterResponse,
  DocListMetadataFiltersResponse,
  DocListRequest,
  DocumentListRequestPayload,
  DocumentListResponse,
  DocumentViewerListIdRequest,
  DocumentViewerListIdResponse,
  DocumentViewerSortConfigRequest,
  DocumentViewerSortConfigResponse,
  DocumentViewerSortRequest,
  DocumentViewerSortResponse,
  DocViewRequest,
  DocViewResponse,
  DuplicateDocumentRequest,
  GetTranslationResponse,
  IAnnotationListGridResponse,
  ICodingFormDetailsForEditResponse,
  ICodingFormGridListResponse,
  ICodingFormMetadataResponse,
  ICodingFormPinParams,
  ICodingFormPinResponse,
  IColumnMetdataResponse,
  IDeleteCodingFormResponse,
  IDocListGridDataResponse,
  IDocumentAudioFilePathRequest,
  IDocumentAudioFilePathResponse,
  IDocumentAudioWaveExistResponse,
  IDocumentAudioWaveRequest,
  IDocumentAudioWaveZoomInOutRequest,
  IDocumentMediaStreamingRequest,
  IDocumentMediaTranscriptRequestBody,
  IDocumentMediaTranscriptRequestQueryParams,
  IDocumentViewerCodingFormListRequest,
  IDocumentViewerCodingFormListResponse,
  IDocumentViewerCodingFormRequest,
  IDocumentViewerCodingFormResponse,
  IFolderListViewResponse,
  IGetAnalyticsDocumentsResponse,
  IGetAnnotationDetailsParams,
  IGetAnnotationDetailsResponse,
  IGetAnnotationRequestParams,
  IGetCodingFormList,
  IGetCodingStamp,
  IGetDocDetailsMetadataGridDataReq,
  IGetDocDetailsMetadataGridDataResItem,
  IGetDocMetadataFilterOptionsList,
  IGetDocMetadataFilterOptionsListRes,
  IGetRenderMapResponse,
  IGetTextDocResponse,
  IKeywordColorListResponse,
  IKeywordHighlightListDataPayload,
  IKeywordHighlightListResponse,
  IRenderThreadMapParams,
  ISaveCodingFormPayload,
  ISaveCodingFormResponse,
  ISaveCodingFormSortOrderPayload,
  ISaveCodingFormSortOrderResponse,
  ISaveRedactionTags,
  ISaveUserCodingStamp,
  ITreeNodeResponse,
  IUserRedactionTagsPayload,
  IUserRedactionTagsResponse,
  LoadViewerRequest,
  LoadViewerResponse,
  PreviewDocumentResponse,
  ManualSortParams,
  ManualSortResponse,
  OptimizedSortRequest,
  OptimizedSortResponse,
  RedactionPanelStatus,
  RedactionTagsResponse,
  Remark,
  RemarkPayload,
  UpsertAnnotationRequest,
  UpsertAnnotationResponse,
  ViewingSessionRequest,
  ViewingSessionResponse,
  ISaveToProfileRequest,
  IDocumentListViewPayload,
  IDocumentListViewResponse,
  ProgressBarRequest,
  RefreshAssignmentProgressRequest,
  AddNavigationNotificationPayload,
  GetDocumentForTagAndFolderResponse,
  GetDocumentForTagAndFolderRequest,
  GetNavigationForMasterListIdResponse,
  GetNavigationForMasterListIdRequest,
  IGetDocumentListIdForActionResponse,
  IGetExportMasterDocListIdRequest,
  IDocumentFilesRequest,
} from '../../api/types/document.type';
import {DocTableRequest} from '../../components/doc-explorer/doc-explorer-data-grid/DocExplorerDataGrid';
import {
  DocColumnUpdatePayload,
  DocCompletedCheckRequest,
  DocCompletedCheckResponse,
  DocExplorerColumnConfig,
  DocExplorerColumnHeader,
  DocMetadataFilter,
  DocTableResponse,
} from '../../shared/interfaces';
import {
  AISummaryForDocumentRequest,
  AISummaryForDocumentResponse,
} from '../../api/types/aiSummarization.type';
import {getAISummaryForDocument} from '../../api/endpoints/aiSummarizationApi';

export const useCodingFormGridList = (queryParams: string) => {
  return useQuery<ICodingFormGridListResponse, Error>({
    queryKey: ['codingFormGridList', queryParams],
    queryFn: () => getCodingFormGridList(queryParams),
  });
};

export const useTreeNodesList = (id: number) => {
  return useQuery<ITreeNodeResponse[] | ITreeNodeResponse, Error>({
    queryKey: ['useTreeNodesList', id],
    queryFn: ({queryKey}) => getTreeNodesById(queryKey[1] as number),
  });
};
export const useCodingFormMetadata = () => {
  return useQuery<ICodingFormMetadataResponse, Error>({
    queryKey: ['codingFormMetadata'],
    queryFn: () => getCodingFormMetadata(),
  });
};

export const useDeleteCodingForm = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IDeleteCodingFormResponse, Error, string>({
    mutationFn: deleteCodingForm,
    mutationKey: ['deleteCodingForm'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCanDeleteCodingForm = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IDeleteCodingFormResponse, Error, string>({
    mutationFn: canDeleteCodingForm,
    mutationKey: ['canDeleteCodingForm'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCopyCodingForm = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IDeleteCodingFormResponse, Error, string>({
    mutationFn: copyCodingForm,
    mutationKey: ['copyCodingForm'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCodingFormDetailsforEdit = (
  codingFormId: string,
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ICodingFormDetailsForEditResponse, Error, string>({
    mutationFn: (id) => getCodingFormDetailsforEdit(id),
    mutationKey: ['codingFormDetailsForEdit', codingFormId],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetDocMetadataFilterQuery = () => {
  return useQuery<DocMetadataFilter[], Error>({
    queryKey: ['docMetadataFilter'],
    queryFn: () => getDocMetadataFilters(),
  });
};

export const useGetConceptResults = (
  docId: string,
  conceptParameter: string,
  scorePercentage: string,
  enabled: boolean
) => {
  return useQuery<any, Error>({
    queryKey: ['getConceptResults', docId, conceptParameter, scorePercentage],
    queryFn: () => getConceptResults(docId, conceptParameter, scorePercentage),
    enabled: enabled,
    placeholderData: undefined,
    staleTime: 0,
    refetchOnMount: 'always',
    networkMode: 'always',
    gcTime: 0,
  });
};

export const useMultipleConceptResults = (
  items: {docId: string; conceptParameter: string; scorePercentage: string}[]
) => {
  const queries = useQueries({
    queries: items.map(({docId, conceptParameter, scorePercentage}) => ({
      queryKey: ['conceptResults', docId, conceptParameter, scorePercentage],
      queryFn: () =>
        getConceptResults(docId, conceptParameter, scorePercentage),
      enabled: !!docId && !!conceptParameter,
      placeholderData: undefined,
      staleTime: 0,
      refetchOnMount: 'always',
      networkMode: 'always',
      gcTime: 0,
    })),
  });

  return queries;
};

export const useGetDocMetadataFilterOptionsQuery = () => {
  return useMutation<
    IGetDocMetadataFilterOptionsListRes,
    Error,
    IGetDocMetadataFilterOptionsList
  >({
    mutationFn: getDocMetadataFilterOptionsList,
    mutationKey: ['getDocMetadataFilterOptionsList'],
  });
};

export const useDocListGrid = (
  onSuccess?: (data: IDocListGridDataResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IDocListGridDataResponse, Error, DocListRequest>({
    mutationFn: (payload) => getDocListGridData(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDocumentList = (
  onSuccess?: (data: DocumentListResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<DocumentListResponse, Error, DocumentListRequestPayload>({
    mutationFn: (payload) => getDocumentList(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useAddNavigationNotification = (
  onSuccess?: (data: number) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<number, Error, AddNavigationNotificationPayload>({
    mutationFn: (payload) => addNavigationNotification(payload),
    mutationKey: ['addNavigationNotification'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
export const useDocumentViewerListId = (
  onSuccess?: (data: DocumentViewerListIdResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    DocumentViewerListIdResponse,
    Error,
    DocumentViewerListIdRequest
  >({
    mutationFn: (payload) => getDocumentViewerListId(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
export const useGetDocExplorerColumnConfigQuery = () => {
  return useQuery<DocExplorerColumnConfig, Error>({
    queryKey: ['docDataGridColumns'],
    queryFn: () => getDocExplorerColumnConfig(),
  });
};

export const useSaveDocColumnConfig = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<boolean, Error, DocColumnUpdatePayload[]>({
    mutationFn: (payload) => saveDocColumnConfig(payload),
    mutationKey: ['docDataGridColumnsSave'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useResetDocColumnConfig = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<boolean, Error, DocExplorerColumnHeader[]>({
    mutationFn: (payload) => resetDocColumnConfig(payload),
    mutationKey: ['docDataGridColumnsReset'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetDocExplorerGridDataQuery = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<DocTableResponse, Error, DocTableRequest>({
    mutationFn: (payload) => getDocExplorerGridData(payload),
    mutationKey: ['DocExplorerGridData'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetDocDetailsMetadataQuery = (
  payload: IGetDocDetailsMetadataGridDataReq,
  enabled: boolean
) => {
  return useQuery<IGetDocDetailsMetadataGridDataResItem[], Error>({
    queryKey: ['DocDetailsMetadataGridData', payload],
    queryFn: () => getDocDetailsMetadataGridData(payload),
    enabled,
  });
};

export const usePreviewDocument = (
  docId: number | string,
  docIdValue: string,
  enabled: boolean
) => {
  return useQuery<PreviewDocumentResponse, Error>({
    queryKey: ['previewDocument', docId, docIdValue],
    queryFn: () => getPreviewDocument(docId, docIdValue),
    enabled,
  });
};

export const useGetFilePath = (
  payload: LoadViewerRequest,
  enabled: boolean
) => {
  return useQuery<LoadViewerResponse, Error>({
    queryKey: [
      'GetFilePath',
      payload.documentId,
      payload.fileType,
      payload.seqNo,
      payload.contextType,
      payload.productionId,
    ],
    queryFn: () => getFilePath(payload),
    enabled: enabled,
    placeholderData: undefined,
    staleTime: 0,
    refetchOnMount: 'always',
    networkMode: 'always',
    gcTime: 0,
  });
};

export const useGetTranslations = (
  documentId: number,
  type: string,
  enabled: boolean
) => {
  return useQuery<GetTranslationResponse, Error>({
    queryKey: ['GetTranslations', documentId, type],
    queryFn: () => getTranslations(documentId, type),
    enabled,
    staleTime: 0,
    placeholderData: undefined,
    refetchOnMount: 'always',
    networkMode: 'always',
    gcTime: 0,
  });
};

export const useGetViewingSession = (
  payload: ViewingSessionRequest | null,
  enabled: boolean
) => {
  return useQuery<ViewingSessionResponse, Error>({
    queryKey: ['GetViewingSessionId', payload?.documentId],
    queryFn: () => getViewingSessionId(payload),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    networkMode: 'always',
    gcTime: 0,
    placeholderData: undefined,
  });
};

export const useGetCodingFormList = () => {
  return useQuery<IGetCodingFormList[], Error>({
    queryKey: ['getCodingFormList'],
    queryFn: () => getCodingFormList(),
  });
};

export const useSaveCodingFormSortOrder = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ISaveCodingFormSortOrderResponse,
    Error,
    ISaveCodingFormSortOrderPayload[]
  >({
    mutationFn: saveCodingFormSortOrder,
    mutationKey: ['saveCodingFormSortOrder'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useSaveCodingForm = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ISaveCodingFormResponse, Error, ISaveCodingFormPayload>({
    mutationFn: saveCodingForm,
    mutationKey: ['saveCodingForm'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetDocListMetadataFilters = (): ReturnType<
  typeof useQuery<DocMetadataFilter[], Error>
> => {
  return useQuery<DocMetadataFilter[], Error>({
    queryKey: ['docListMetadataFilter'],
    queryFn: () => getDocListmetadataFilters(),
  });
};

export const useGetDocListLoadmetaDataFilters = (queryParams: string) => {
  return useQuery<DocListMetadataFiltersResponse, Error>({
    queryKey: ['DocListLoadmetadataFilters', queryParams],
    queryFn: () => getMetadataListDoclistFilters(queryParams),
  });
};

export const useCreateMasterDocList = (
  onSuccess?: (data: DocExplorerMasterResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    DocExplorerMasterResponse,
    Error,
    DocExplorerMasterRequest
  >({
    mutationFn: createMasterDocList,
    mutationKey: ['createMasterDocList'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCreateDocView = (
  onSuccess?: (data: DocViewResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<DocViewResponse, Error, DocViewRequest>({
    mutationFn: createDocView,
    mutationKey: ['createDocView'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDocumentViewerSort = (
  onSuccess?: (data: DocumentViewerSortResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    DocumentViewerSortResponse,
    Error,
    DocumentViewerSortRequest
  >({
    mutationFn: postDocumentViewerSort,
    mutationKey: ['documentViewerSort'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDocumentViewerOptimizedSort = (
  onSuccess?: (data: OptimizedSortResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<OptimizedSortResponse, Error, OptimizedSortRequest>({
    mutationFn: postDocumentViewerOptimizedSort,
    mutationKey: ['documentViewerOptimizedSort'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDocumentViewerManualSort = (
  onSuccess?: (data: ManualSortResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ManualSortResponse, Error, ManualSortParams>({
    mutationFn: getDocumentViewerManualSort,
    mutationKey: ['documentViewerManualSort'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDocumentViewerSortConfig = (
  onSuccess?: (data: DocumentViewerSortConfigResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    DocumentViewerSortConfigResponse,
    Error,
    DocumentViewerSortConfigRequest
  >({
    mutationFn: postDocumentViewerSortConfig,
    mutationKey: ['documentViewerSortConfig'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDocListMetaData = () => {
  return useQuery<IColumnMetdataResponse, Error>({
    queryKey: ['getDocListMetaData'],
    queryFn: () => getDocListMetaData(),
  });
};

export const useGetFolderListView = (docId: number, enabled = true) => {
  return useQuery<IFolderListViewResponse[], Error>({
    queryKey: ['useGetFolderListView', docId],
    queryFn: ({queryKey}) => getFolderListView(queryKey[1] as number),
    enabled,
  });
};

export const useAnnotationListGrid = (
  onSuccess?: (data: IAnnotationListGridResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IAnnotationListGridResponse,
    Error,
    IGetAnnotationRequestParams
  >({
    mutationFn: (payload) => getAnnotationListGrid(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetKeywordHighlightList = (
  queryParams: IKeywordHighlightListDataPayload
) => {
  return useQuery<IKeywordHighlightListResponse, Error>({
    queryKey: ['getKeywordHighlightList', queryParams],
    queryFn: () => getKeywordHighlightList(queryParams),
  });
};
export const useGetDuplicateDocumentsForSelectedDocumentId = (
  payload: DuplicateDocumentRequest,
  enabled: boolean
) => {
  return useQuery<string, Error>({
    queryKey: [
      'useGetDuplicateDocumentsForSelectedDocumentId',
      payload.documentId,
    ],
    queryFn: () => getDuplicateDocumentsForSelectedDocumentId(payload),
    enabled: enabled,
    placeholderData: undefined,
    staleTime: 0,
    refetchOnMount: 'always',
    networkMode: 'always',
    gcTime: 0,
  });
};

export const useDeleteAnnotation = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: deleteAnnotation,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDeleteKeywordHighlight = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: deleteKeywordHightlight,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetDocDownloadFormats = (param: string) => {
  return useQuery<any[], Error>({
    queryKey: ['getDocDownloadFormats'],
    queryFn: () => getDocDownloadFormats(param),
    enabled: false,
  });
};

export const useGetDoNotPrompt = () => {
  return useQuery<string, Error>({
    queryKey: ['getDefaultRedactionPrompt'],
    queryFn: () => getDefaultRedactionPrompt(),
  });
};
export const useGetRedactionTags = (enabled: boolean) => {
  return useQuery<RedactionTagsResponse, Error>({
    queryKey: ['getRedactionTags'],
    queryFn: () => getRedactionTags(),
    staleTime: 0,
    enabled,
  });
};

export const useGetRemarks = (documentId: number) => {
  return useQuery<Remark[], Error>({
    queryKey: ['getRemarks'],
    queryFn: () => getRemarks(documentId),
    staleTime: 0,
  });
};

export const useAddRemark = (
  onSuccess?: (data: any) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, RemarkPayload>({
    mutationFn: (payload) => addRemarks(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDeleteRemark = (
  onSuccess?: (data: any) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, Partial<RemarkPayload>>({
    mutationFn: (payload) => deleteRemarks(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useSetDoNotPrompt = (
  onSuccess?: (data: boolean) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<boolean, Error, boolean>({
    mutationFn: (payload) => setDefaultRedactionPrompt(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUpsertAnnotation = (
  onSuccess?: (data: UpsertAnnotationResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<UpsertAnnotationResponse, Error, UpsertAnnotationRequest>({
    mutationFn: upsertAnnotation,
    mutationKey: ['addAnnotation'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useAnnotationDetails = (params: IGetAnnotationDetailsParams) => {
  return useQuery<IGetAnnotationDetailsResponse, Error>({
    queryKey: ['useAnnotationDetails', params.annotationId],
    queryFn: () => getAnnotationDetails(params),
    enabled: false,
  });
};

export const useRenderThreadMap = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IGetRenderMapResponse, Error, IRenderThreadMapParams>({
    mutationFn: renderThreadMap,
    mutationKey: ['renderThreadMap'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetReductionPropagation = () => {
  return useQuery<boolean, Error>({
    queryKey: ['useGetReductionPropagation'],
    queryFn: () => getRedactionPropagation(),
  });
};
export const useDocumentCodingFormList = (
  params: IDocumentViewerCodingFormListRequest
) => {
  return useQuery<IDocumentViewerCodingFormListResponse[], Error>({
    queryKey: ['useDocumentCodingFormList'],
    queryFn: () => getDocumentViewerCodingFormList(params),
    enabled: true,
  });
};

export const useDocumentCodingForm = (
  params: IDocumentViewerCodingFormRequest,
  enabled: boolean
) => {
  return useQuery<IDocumentViewerCodingFormResponse, Error>({
    queryKey: ['useDocumentCodingForm', params],
    queryFn: () => getDocumentViewerCodingForm(params),
    enabled,
  });
};

export const useGetRedactionPanelStatus = (
  onSuccess?: (data: RedactionPanelStatus[]) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<RedactionPanelStatus[], Error, number>({
    mutationKey: ['redactionPanelStatus'],
    mutationFn: (documentId: number) => getRedactionPanelStatus(documentId),

    onSuccess: (data) => {
      onSuccess?.(data);
    },

    onError: (err) => {
      onError?.(err);
    },
  });
};
export const usePinCodingForm = (
  onSuccess?: (data: ICodingFormPinResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ICodingFormPinResponse, Error, ICodingFormPinParams>({
    mutationFn: pinCodingForm,
    mutationKey: ['pinCodingForm'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetUserRedactionTags = (
  payload: IUserRedactionTagsPayload,
  enabled: boolean
) => {
  return useQuery<IUserRedactionTagsResponse[], Error>({
    queryKey: ['getUserRedactionTags', payload.documentID, payload.nodeID],
    queryFn: () => getUserRedactionTags(payload),
    staleTime: 0,
    enabled,
  });
};

export const useSaveRedactionTags = (
  onSuccess?: (data: boolean) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<boolean, Error, ISaveRedactionTags>({
    mutationFn: (payload: ISaveRedactionTags) => saveRedactionTags(payload),
    mutationKey: ['saveRedactionTags'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
export const useKeywordDropdownColorList = () => {
  return useQuery<IKeywordColorListResponse>({
    queryKey: ['keywordHighlightColorList'],
    queryFn: getKeywordHighlightColorList,
  });
};

export const useGetKeywordHighlightDetails = (
  keywordId: number | string | null
) => {
  return useQuery({
    queryKey: ['keyword-highlight-details', keywordId],
    queryFn: async () => getKeywordDetails({keywordId}),
    enabled: !!keywordId,
  });
};

export const useAddKeywordHighlight = () => {
  return useMutation<string, Error, any>({
    mutationFn: addKeywordHighlight,
  });
};

export const useAudioFilePath = () => {
  return useMutation<
    IDocumentAudioFilePathResponse,
    AxiosError,
    IDocumentAudioFilePathRequest
  >({
    mutationFn: getAudioFilePath,
    mutationKey: ['audioFilePath'],
  });
};

export const useAudioWaveExits = () => {
  return useMutation<
    IDocumentAudioWaveExistResponse,
    AxiosError,
    IDocumentAudioWaveRequest
  >({
    mutationFn: getAudioWaveExist,
    mutationKey: ['audioWaveExist'],
  });
};

export const useAudioWaveZoomInOut = () => {
  return useMutation<Blob, AxiosError, IDocumentAudioWaveZoomInOutRequest>({
    mutationFn: getAudioWaveZoomInOut,
    mutationKey: ['audioWaveZoomInOut'],
  });
};

export const useMediaForStreaming = () => {
  return useMutation<Blob, AxiosError, IDocumentMediaStreamingRequest>({
    mutationFn: getMediaForStreaming,
    mutationKey: ['mediaForStreaming'],
  });
};

export const useDocumentDownloadFiles = (payload: IDocumentFilesRequest) => {
  return useQuery<Blob, Error>({
    queryKey: ['getDocumentDownloadFiles', payload],
    queryFn: () => getDocumentFiles(payload),
    enabled: !!payload.filesPath,
    retry: 2,
  });
};

export const useGetAnalyticsDocuments = (payload: analyticsDocPayload) => {
  return useQuery<IGetAnalyticsDocumentsResponse, Error>({
    queryKey: ['getAnalyticsDocuments', payload],
    queryFn: () => getAnalyticsDocuments(payload),
    retry: 2,
  });
};

export const useGetCodingStamp = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<string, Error, IGetCodingStamp>({
    mutationFn: getCodingStamp,
    mutationKey: ['getCodingStamp'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
export const useGetEditCodingStamp = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<[], Error, any>({
    mutationFn: getEditCodingStamp,
    mutationKey: ['getEditCodingStamp'],

    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useSaveUserCodingStamp = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, ISaveUserCodingStamp>({
    mutationFn: saveUserCodingStamp,
    mutationKey: ['saveUserCodingStamp'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
export const useGetEditCodingDetails = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<[], Error, any>({
    mutationFn: getEditCodingDetails,
    mutationKey: ['getEditCodingDetails'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetRefreshCodingStamp = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<string, Error, IGetCodingStamp>({
    mutationFn: getRefreshCodingStamp,
    mutationKey: ['getRefreshCodingStamp'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useTextViewDoc = (docId: number, enabled = true) => {
  return useQuery<number, Error, IGetTextDocResponse>({
    queryKey: ['getTextViewDoc', docId],
    queryFn: () => getTextViewDoc(docId),
    retry: 2,
    enabled,
  });
};
//saveEditCodingStamp
export const useSaveEditCodingStamp = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ISaveUserCodingStamp, Error, any>({
    mutationFn: saveEditCodingStamp,
    mutationKey: ['saveEditCodingStamp'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDeleteCodingStamp = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ISaveUserCodingStamp, Error, any>({
    mutationFn: deleteCodingStamp,
    mutationKey: ['deleteCodingStamp'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDocumentCompleteCheck = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    DocCompletedCheckResponse,
    Error,
    DocCompletedCheckRequest
  >({
    mutationFn: getDocIsCompleted,
    mutationKey: ['documentCompleteCheck'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDocumentMediaTranscript = (
  onSuccess?: (data: string) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    string,
    Error,
    {
      queryParams: IDocumentMediaTranscriptRequestQueryParams;
      requestBody: IDocumentMediaTranscriptRequestBody;
    }
  >({
    mutationKey: ['getMediaTranscript'],
    mutationFn: (params: {
      queryParams: IDocumentMediaTranscriptRequestQueryParams;
      requestBody: IDocumentMediaTranscriptRequestBody;
    }) => getMediaTranscript(params.queryParams, params.requestBody),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useSaveToProfile = (
  onSuccess?: (data: any) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, ISaveToProfileRequest>({
    mutationFn: (payload) => saveToProfile(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDocumentListView = (
  onSuccess?: (data: IDocumentListViewResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IDocumentListViewResponse,
    Error,
    IDocumentListViewPayload
  >({
    mutationFn: (payload) => getDocumentListView(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCodeSameAsPrevious = (
  onSuccess?: (data: any) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: (payload) => codeSameAsPrevious(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useProgressBar = (
  onSuccess?: (data: number) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<number, Error, ProgressBarRequest>({
    mutationFn: (payload) => getProgressBar(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useRefreshAssignmentProgress = (
  onSuccess?: (data: number) => void
) => {
  return useMutation({
    mutationFn: (payload: RefreshAssignmentProgressRequest) =>
      getRefreshAssignmentProgress(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
  });
};

export const useGetDocumentForTagAndFolder = (
  onSuccess?: (data: GetDocumentForTagAndFolderResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    GetDocumentForTagAndFolderResponse,
    Error,
    GetDocumentForTagAndFolderRequest
  >({
    mutationFn: (payload) => getDocumentForTagAndFolder(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetNavigationMasterListId = (
  onSuccess?: (data: GetNavigationForMasterListIdResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    GetNavigationForMasterListIdResponse,
    Error,
    GetNavigationForMasterListIdRequest
  >({
    mutationFn: (payload) => getNavigationMasterListId(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetDocumentListIdforAction = (
  onSuccess?: (data: IGetDocumentListIdForActionResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IGetDocumentListIdForActionResponse,
    Error,
    IGetExportMasterDocListIdRequest
  >({
    mutationFn: (payload) => getDocumentListIdForAction(payload),
    mutationKey: ['getDocumentListIdForAction'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetAISummaryForDocument = (
  payload: AISummaryForDocumentRequest,
  enabled: boolean
) => {
  return useQuery<AISummaryForDocumentResponse, Error>({
    queryKey: ['getAISummaryForDocument', payload.docIds[0]],
    queryFn: () => getAISummaryForDocument(payload),
    enabled,
  });
};
